import cx from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Breadcrumbs } from '@bwoty-web/ui-kit';
import { bindActionCreators } from 'redux';
import { useEffect, useMemo } from 'react';
import { useIsGlobetrotter } from '@bwoty-web/ui-kit/hooks';

import FadeIn from '../utils/fadeIn';
import ErrorPopup from '../components/shared/errorPopup/ErrorPopup';
import RoomCardList from '../components/priceMatrix/RoomCardList';
import MobileHeader from '../components/priceMatrix/MobileHeader';
import PriceDetails from '../components/shared/priceDetails/PriceDetails';
import PriceSummary from '../components/shared/priceSummary/PriceSummary';
import LoaderHeading from '../components/shared/loaders/LoaderHeading';
import GlobalContext from '../context/globalContext';
import PriceMatrixTable from '../components/priceMatrix/PriceMatrixTable';
import MessageContainer from '../components/shared/message/MessageContainer';
import PriceMatrixLoader from '../components/priceMatrix/loaders/PriceMatrixLoader';
import AlternativeHotels from '../components/priceMatrix/AlternativeHotels';
import { texts } from '../assets/texts';
import { urlUtils } from '../utils/url';

import * as PriceMatrixActions from '../redux/actions/priceMatrixActions';

import './priceMatrixContainer.scss';

const PriceMatrixContainer = ({
  actions,
  alternativeHotels,
  alternativeHotelsLabels,
  appStatus,
  error,
  hotelData,
  labels,
  message,
  searchInformation,
  siteSettings,
  summaryReturnLink,
  transportData,
  userId,
}) => {
  const isGlobetrotter = useIsGlobetrotter();
  useEffect(() => {
    actions.init();
  }, [actions]);

  const globals = useMemo(
    () => ({
      labels,
      siteSettings,
    }),
    [siteSettings, labels],
  );

  const showLoader = !labels || !appStatus.packageDataInitialized;
  const isCombination = urlUtils.isCombination();
  const isHotelOnly = urlUtils.getTypeFromUrl() === 'IndependentHotelOnly';

  if (showLoader) {
    if (isCombination) {
      return (
        <LoaderHeading
          heading={texts[siteSettings.siteId].loaderHeadingShort}
          topMargin
        />
      );
    }

    return (
      <div>
        <PriceMatrixLoader
          isGlobetrotter={isGlobetrotter}
          isHotelOnly={isHotelOnly}
        />
      </div>
    );
  }

  const showPriceMatrix =
    !!hotelData?.selectedRoomGroup && (isHotelOnly || !!transportData);

  const showAlternativeHotels =
    !hotelData?.roomGroups && alternativeHotels != null;

  const { selectedRoomGroup } = hotelData ?? {};

  const clickBook = () => {
    if (selectedRoomGroup.isReadyForPassengerDetails) {
      actions.addToCart(searchInformation, selectedRoomGroup);
    } else {
      window.location = urlUtils.standaloneUrlHandler(
        `/${selectedRoomGroup.link}`,
      );
    }
  };

  const renderPriceDetails = () => {
    return (
      <PriceDetails
        selectedRoomGroup={selectedRoomGroup}
        hotelInformation={{
          ...hotelData,
          roomDescription: hotelData.selectedRoomGroup.rooms,
          amenitiesDescription: hotelData.selectedRoomGroup?.amenities
            ?.map((amenity) => amenity?.name)
            ?.join(', '),
        }}
        priceBreakDown={selectedRoomGroup.priceBreakDowns}
        searchInformation={searchInformation}
        labels={labels.priceDetails}
        outBoundInformation={transportData?.outBoundFlights[0]}
        isSummaryView
      />
    );
  };

  const priceSummaryLabels = {
    button: isCombination ? labels.roomSelectBtnLabel : labels.nextStepAndBook,
    totalPrice: labels.totalPrice,
    priceDetails: labels.priceDetailsLinkLabel,
  };

  const productAndPaxInfo = `${labels.productsIncludedParsed}, ${searchInformation?.passengerInfo}`;

  return (
    <GlobalContext.Provider value={globals}>
      <FadeIn>
        <div className={cx({ 'iw-price-matrix--hotel-only': isHotelOnly })}>
          <MessageContainer
            message={message}
            user={userId}
            labels={labels}
            style={{ marginBottom: 20 }}
          />
          {isCombination && (
            <>
              <Breadcrumbs
                className="iw-price-matrix__breadcrumbs"
                links={[
                  {
                    name: labels.iwGeneral.breadcrumbComboSummaryLinkLabel,
                    uri: urlUtils.standaloneUrlHandler(summaryReturnLink),
                    target: null,
                  },
                  {
                    name: labels.combinationHeader,
                    uri: window.location.href,
                    target: null,
                  },
                ]}
              />
              <h1 className="iw-price-matrix__combination-heading">
                {labels.combinationHeader}
              </h1>
              {isGlobetrotter && (
                <div className="iw-price-matrix__heading-divider" />
              )}
            </>
          )}
          {isHotelOnly && (
            <h3 className="iw-price-matrix-hotel-only--dates-heading">
              {`${hotelData?.checkIn} - ${hotelData?.checkOut}`}
            </h3>
          )}
          {hotelData?.roomGroups?.length > 0 &&
            (isGlobetrotter || isHotelOnly ? (
              <RoomCardList
                hotel={hotelData}
                isHotelOnly={isHotelOnly}
                labels={labels}
                selectRoomGroup={actions.selectRoomGroup}
                showRoomTypeLink={siteSettings?.showRoomTypeLink}
              />
            ) : (
              <>
                {!isCombination && (
                  <MobileHeader
                    labels={labels}
                    transport={transportData}
                    getFlightTime={actions.getFlightTime}
                  />
                )}
                <PriceMatrixTable
                  getFlightTime={actions.getFlightTime}
                  hotel={hotelData}
                  labels={labels}
                  selectRoomGroup={actions.selectRoomGroup}
                  showRoomTypeLink={siteSettings?.showRoomTypeLink}
                  transport={transportData}
                />
              </>
            ))}
          {showPriceMatrix && (
            <PriceSummary
              labels={priceSummaryLabels}
              productAndPaxInfo={productAndPaxInfo}
              totalPrice={
                hotelData?.selectedRoomGroup?.priceBreakDowns?.totalPrice
              }
              onNavigate={() => clickBook()}
              priceDetails={renderPriceDetails()}
              disclaimer={
                hotelData.selectedRoomGroup.hasCharges
                  ? labels.paymentText
                  : null
              }
              loading={hotelData.loading}
              discounted={selectedRoomGroup?.priceBreakDowns?.isDiscounted}
            />
          )}
          {showAlternativeHotels && (
            <AlternativeHotels
              hotels={alternativeHotels}
              alternativeHotelsLabels={alternativeHotelsLabels}
              transportData={transportData}
              searchInformation={searchInformation}
              labels={labels}
              siteSettings={siteSettings}
            />
          )}
          {error.visible && (
            <ErrorPopup
              closePopup={() => actions.toggleErrorPopup({ visible: false })}
              labels={labels.errors}
              error={error}
            />
          )}
        </div>
      </FadeIn>
    </GlobalContext.Provider>
  );
};

PriceMatrixContainer.propTypes = {
  actions: PropTypes.shape({
    addToCart: PropTypes.func,
    getFlightTime: PropTypes.func,
    init: PropTypes.func,
    selectRoomGroup: PropTypes.func,
    toggleAddToCartError: PropTypes.func,
    toggleErrorPopup: PropTypes.func,
  }).isRequired,
  alternativeHotels: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  alternativeHotelsLabels: PropTypes.shape({}),
  appStatus: PropTypes.shape({
    packageDataInitialized: PropTypes.bool,
  }).isRequired,
  error: PropTypes.shape({
    buttonUrl: PropTypes.string,
    message: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
    visible: PropTypes.bool,
  }).isRequired,
  hotelData: PropTypes.shape({
    checkIn: PropTypes.string,
    checkOut: PropTypes.string,
    loading: PropTypes.bool,
    roomGroups: PropTypes.arrayOf(PropTypes.shape({})),
    selectedRoomGroup: PropTypes.shape({
      amenities: PropTypes.arrayOf(),
      hasCharges: PropTypes.bool,
      isReadyForPassengerDetails: PropTypes.bool,
      link: PropTypes.string,
      priceBreakDowns: PropTypes.shape({
        isDiscounted: PropTypes.bool,
        totalPrice: PropTypes.string,
      }),
      rooms: PropTypes.arrayOf(),
    }),
    showAddToCartError: PropTypes.bool,
  }).isRequired,
  labels: PropTypes.shape({
    combinationHeader: PropTypes.string,
    errors: PropTypes.shape(),
    iwGeneral: PropTypes.shape({
      breadcrumbComboSummaryLinkLabel: PropTypes.string,
    }),
    nextStepAndBook: PropTypes.string,
    paymentText: PropTypes.string,
    priceDetails: PropTypes.shape(),
    priceDetailsLinkLabel: PropTypes.string,
    productsIncludedParsed: PropTypes.string,
    roomSelectBtnLabel: PropTypes.string,
    totalPrice: PropTypes.string,
  }).isRequired,
  message: PropTypes.shape({
    message: PropTypes.string,
    severity: PropTypes.string,
    title: PropTypes.string,
  }),
  searchInformation: PropTypes.shape({
    passengerInfo: PropTypes.string,
    sessionId: PropTypes.string,
  }).isRequired,
  siteSettings: PropTypes.shape({
    imageDomain: PropTypes.string.isRequired,
    showRoomTypeLink: PropTypes.bool,
    siteId: PropTypes.number,
  }).isRequired,
  summaryReturnLink: PropTypes.string,
  transportData: PropTypes.shape({
    outBoundFlights: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  userId: PropTypes.string.isRequired,
};

PriceMatrixContainer.defaultProps = {
  alternativeHotelsLabels: {},
  message: null,
  summaryReturnLink: '',
};

const mapActions = (dispatch) => {
  return {
    actions: bindActionCreators({ ...PriceMatrixActions }, dispatch),
  };
};

const mapStateToProps = (state) => {
  return {
    alternativeHotels: state.alternativeHotels.hotels,
    alternativeHotelsLabels: state.labels.alternativeHotels,
    appStatus: state.appStatus,
    error: state.error,
    hotelData: state.hotelData,
    labels: state.labels.priceMatrix,
    message: state.message,
    searchInformation: state.searchInformation,
    siteSettings: state.common.siteSettings,
    summaryReturnLink: state.combinationInfo?.summaryReturnLink,
    transportData: state.transportData,
    userId: state.common.userId,
  };
};

export default connect(mapStateToProps, mapActions)(PriceMatrixContainer);
